import { useEffect, useState } from 'react';

/**
 * Returns the id of the section currently in view, for nav highlighting.
 *
 * Uses IntersectionObserver rather than a scroll listener so the browser does
 * the work off the main thread. The top margin offsets the sticky header, and
 * the bottom margin biases toward whichever section occupies the upper part of
 * the viewport — otherwise the last short section on the page can never win.
 */
export function useScrollSpy(ids: string[], headerOffset = 88): string | null {
  const [activeId, setActiveId] = useState<string | null>(ids[0] ?? null);

  useEffect(() => {
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) setActiveId(visible[0].target.id);
      },
      {
        rootMargin: `-${headerOffset}px 0px -55% 0px`,
        threshold: 0,
      },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [ids, headerOffset]);

  return activeId;
}
